import {
  DEG2RAD,
  conjugate,
  mul,
  normalizeQuat,
  quatFromAxisAngle,
  rotateVec,
  sub,
  type Quat,
  type Vec3,
} from './mathx';

/** Shared Phase-1 observation-camera projection defaults. `fovDeg` is vertical. */
export const SCOUT_CAMERA_CONFIG = Object.freeze({
  fovDeg: 80,
  aspect: 16 / 9,
  near: 0.1,
  far: 4000,
  tiltDeg: 20,
});

/**
 * Camera-to-world pose using the simulation's FLU axes: local +X is image
 * forward, +Y is image left, and +Z is image up.
 */
export interface CameraPose {
  position: Vec3;
  orientation: Quat;
}

export interface ScoutCameraState {
  posEnu: Vec3;
  /** Body-to-world quaternion. */
  attitude: Quat;
}

/**
 * Return the rigid scout camera pose. Positive `camTiltDeg` is an uptilt, so
 * the camera's local +X rotates toward body +Z around the body pitch axis.
 */
export const scoutCameraPose = (
  scoutState: ScoutCameraState,
  camTiltDeg: number = SCOUT_CAMERA_CONFIG.tiltDeg,
): CameraPose => {
  const mountRotation = quatFromAxisAngle({ x: 0, y: 1, z: 0 }, -camTiltDeg * DEG2RAD);
  return {
    position: { ...scoutState.posEnu },
    orientation: normalizeQuat(mul(scoutState.attitude, mountRotation)),
  };
};

/** Pure perspective-frustum containment for a world-ENU point. */
export const isPointInFrustum = (
  cameraPose: CameraPose,
  fovDeg: number,
  aspect: number,
  near: number,
  far: number,
  pointEnu: Vec3,
): boolean => {
  if (
    !Number.isFinite(fovDeg) ||
    fovDeg <= 0 ||
    fovDeg >= 180 ||
    !Number.isFinite(aspect) ||
    aspect <= 0 ||
    !Number.isFinite(near) ||
    near < 0 ||
    !Number.isFinite(far) ||
    far <= near
  ) {
    return false;
  }

  const cameraPoint = rotateVec(
    conjugate(normalizeQuat(cameraPose.orientation)),
    sub(pointEnu, cameraPose.position),
  );
  const depth = cameraPoint.x;
  if (depth < near || depth > far) return false;

  const verticalLimit = depth * Math.tan((fovDeg * DEG2RAD) / 2);
  const horizontalLimit = verticalLimit * aspect;
  return Math.abs(cameraPoint.y) <= horizontalLimit && Math.abs(cameraPoint.z) <= verticalLimit;
};
